/**
 * Get Started Checklist Constants
 *
 * Step definitions and mappings between backend flags and checklist steps.
 */

import type { CompanyPayment } from '@scio3/shared-types';
import type { ChecklistStep } from './types';
import { StepId } from './types';

/**
 * Step definitions for the onboarding checklist.
 * Order determines display order in the widget.
 */
export const CHECKLIST_STEPS: ChecklistStep[] = [
  {
    id: StepId.GET_SCREEN_SETUP,
    titleKey: 'getStarted.steps.getScreenSetup.title',
    route: '/app/screenManagement',
    required: true,
    directions: [
      'getStarted.steps.getScreenSetup.directions.downloadApp',
      'getStarted.steps.getScreenSetup.directions.openApp',
    ],
  },
  {
    id: StepId.PAIR_SCREEN,
    titleKey: 'getStarted.steps.pairScreenAndContent.title',
    route: '/app/screenManagement',
    required: true,
    directionGroups: [
      {
        settingKey: 'firstDevicePaired',
        directions: [
          'getStarted.steps.pairScreenAndContent.directions.goToScreensPage',
          'getStarted.steps.pairScreenAndContent.directions.clickNewScreen',
          'getStarted.steps.pairScreenAndContent.directions.enterPairingCode',
        ],
      },
      {
        settingKey: 'firstScreenAssignmentEventFired',
        directions: ['getStarted.steps.pairScreenAndContent.directions.pushContent'],
      },
    ],
  },
  {
    id: StepId.CREATE_CONTENT,
    titleKey: 'getStarted.steps.createContent.title',
    route: '/app/assetManagement',
    required: true,
    directions: [
      'getStarted.steps.createContent.directions.goToAssetsPage',
      'getStarted.steps.createContent.directions.clickAddNew',
      'getStarted.steps.createContent.directions.createContent',
    ],
  },
  {
    id: StepId.CREATE_PLAYLIST,
    titleKey: 'getStarted.steps.createPlaylist.title',
    route: '/app/playlistManagement',
    required: false,
    directionGroups: [
      {
        settingKey: 'firstPlaylistCreated',
        directions: [
          'getStarted.steps.createPlaylist.directions.goToPlaylistsPage',
          'getStarted.steps.createPlaylist.directions.clickNewPlaylist',
        ],
      },
      {
        settingKey: 'firstPlaylistContentAdded',
        directions: ['getStarted.steps.createPlaylist.directions.addAssets'],
      },
    ],
  },
];

/**
 * Mapping from StepId to the backend payment flag field name(s).
 * A step is considered complete when ANY of its mapped flags is "triggered"
 * — flipping a single flag in Mongo cleans the whole step.
 */
export const STEP_FLAG_MAPPING: Record<StepId, (keyof CompanyPayment)[]> = {
  // "Get your screen set up" completes on ANY of three signals. Only the first is
  // a payment flag; the other two are OR'd in by useGetStartedChecklist:
  //   1. a device pairs — `firstDevicePaired` (here)
  //   2. the user clicks the download link — `screenSetupDownloadClicked` (client-side)
  //   3. the account has preconfigured screens — `company.noOfScreens > 0`, which
  //      Mass Add Screens bumps WITHOUT setting any pairing timestamp
  [StepId.GET_SCREEN_SETUP]: ['firstDevicePaired'],
  // Pair Screen completes ONLY when content has been assigned to a screen
  // (`firstScreenAssignmentEventFired`). Pairing a screen alone
  // (`firstDevicePaired`) must NOT check the step off — that flag drives the
  // direction-group display only.
  [StepId.PAIR_SCREEN]: ['firstScreenAssignmentEventFired'],
  [StepId.CREATE_CONTENT]: ['firstContentCreated'],
  [StepId.CREATE_PLAYLIST]: ['firstPlaylistCreated'],
};

/** Value indicating a flag has not been triggered yet */
export const NOT_TRIGGERED = 'not_triggered';

/**
 * Wistia video ID for the getting started video — new SCIO 3.0 demo
 * "OptiSigns Demo - 6 min" (https://optisigns.wistia.com/s/9g05f8wsbh6bvr8 → embed hashed id).
 * scio1 keeps its own old video; this only updates scio3 surfaces (checklist + Help menu).
 */
export const GETTING_STARTED_VIDEO_ID = 'f6z3p6qifd';

/**
 * Check if a flag value indicates the step is completed.
 * A step is complete if the flag is set AND is not 'not_triggered'.
 */
export function isFlagCompleted(value: string | undefined): boolean {
  return Boolean(value) && value !== NOT_TRIGGERED;
}
